// Feed impression coalescing — the layer that makes impressions affordable.
//
// An impression is generated roughly every few hundred milliseconds of scrolling.
// Putting that on the wire per entity is the entire risk in this feature, so
// nothing here sends anything: it holds a deduplicated set of entities and hands
// ONE array event to the shared telemetry buffer per flush. Two separate
// reductions are stacked, and both matter:
//
//   1. Set semantics — an entity seen twice in a session is recorded once, so
//      scrolling back up over a feed adds nothing.
//   2. Array batching — a flush of 200 entities is ONE `/api/track/batch` event,
//      not 200. Request rate is therefore set by the flush interval and the
//      number of open feed tabs, and is INDEPENDENT of scroll speed.
//
// The interval is deliberately much longer than the shared buffer's own 3s: an
// impression is the lowest-value event on the site and the highest-volume, so it
// is the one event class that should never arm a fast timer. It rides out on
// whatever flush happens next, and on tab-hide via sendBeacon.
import { generateToken } from '~/utils/string-helpers';
import { enqueueTrackEvent, flushTrackEvents } from '~/components/TrackView/trackEventBuffer';
import { IMPRESSION_ENTITIES_MAX, IMPRESSION_SURFACES } from '~/server/schema/track.schema';
import type { ImpressionEntityType, ImpressionSurface } from '~/server/schema/track.schema';

// 90s of coalescing. This sets the STEADY-STATE request cost: N actively
// scrolling tabs cost N/90 requests per second, regardless of scroll speed,
// because a flush describes a set rather than a stream. At the shared buffer's
// own 3s it would be thirty times that for the same information.
//
// It is not the whole story — see the note above `bindLifecycleListeners` for the
// tab-switch path the interval does not govern, and the sizing section of
// docs/features/feed-impressions.md for what that adds up to.
const FLUSH_INTERVAL_MS = 90_000;

// Flush early once a flush would fill an event. Purely a bound on how large one
// request gets — a fast scroller hits this before the interval.
const FLUSH_AT_SIZE = IMPRESSION_ENTITIES_MAX;

// Ceiling on the session-dedupe set. A long-lived tab that scrolls tens of
// thousands of entities would otherwise grow this without bound. Past the cap the
// set is cleared rather than grown, which can re-count an entity seen much
// earlier in a very long session; that is the intended trade against a leak.
const SEEN_CAP = 20_000;

// Random per-tab token. NOT an identifier and never persisted — it exists so the
// server can tell "the same tab saw this entity" from "two tabs saw it", which is
// what makes the number deduplicable at read time despite an at-least-once
// transport.
let sessionKey: string | null = null;
function getSessionKey(): string {
  if (!sessionKey) sessionKey = generateToken(16);
  return sessionKey;
}

type EntityKey = string;
const entityKey = (entityType: ImpressionEntityType, entityId: number): EntityKey =>
  `${entityType}:${entityId}`;

const seen = new Set<EntityKey>();
// surface -> entity key -> entity. Keyed by surface because one flush event
// carries a single surface, and a tab can move between feeds within an interval.
const pending = new Map<
  ImpressionSurface,
  Map<EntityKey, { entityType: ImpressionEntityType; entityId: number }>
>();
let pendingCount = 0;
let timer: ReturnType<typeof setTimeout> | null = null;
let listenersBound = false;

function clearTimer() {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
}

function scheduleTimer() {
  if (timer !== null) return;
  timer = setTimeout(() => {
    timer = null;
    flushImpressions();
  }, FLUSH_INTERVAL_MS);
}

const SURFACES = new Set<string>(IMPRESSION_SURFACES);

// Map a pathname to the closed surface enum. Anything unrecognised is 'other'
// rather than a new value — the column is LowCardinality and the point of the
// enum is that this function cannot widen it.
export function getImpressionSurface(pathname: string): ImpressionSurface {
  if (pathname === '/') return 'home';
  // The first segment names the surface for both a feed (/images) and a detail
  // page hosting one (/models/123 — its gallery is a feed of images).
  const first = pathname.split('/')[1];
  if (first === 'search') return 'search';
  return first && SURFACES.has(first) ? (first as ImpressionSurface) : 'other';
}

// The surface is read from `window.location` rather than passed in from a
// component. Taking it from `useRouter` would subscribe every card on the page to
// route changes, and a router subscription re-renders straight through
// `React.memo` — on a feed that is hundreds of cards re-rendering per navigation,
// which is a steep price for a telemetry label.
export function getCurrentImpressionSurface(): ImpressionSurface {
  return getImpressionSurface(window.location.pathname);
}

// Record that an entity was seen. Synchronous, never throws, no-ops on repeats.
//
// Pass `surface` when it was captured earlier than now. The dwell timer does:
// opening the image-detail dialog rewrites the URL while the feed stays mounted
// and intersecting underneath, so a surface read when the timer FIRES labels
// cards seen on /hubs or /models as `images`.
export function recordImpression(
  entityType: ImpressionEntityType,
  entityId: number,
  surface?: ImpressionSurface
): void {
  if (typeof window === 'undefined') return;
  surface ??= getCurrentImpressionSurface();
  const key = entityKey(entityType, entityId);
  if (seen.has(key)) return;

  // Clearing `seen` while `pending` still holds those keys would let the same key
  // be counted into `pendingCount` twice while the Map absorbs the second write,
  // so the size cap would fire early on a count that no longer matches the buffer.
  // Flushing first keeps the two in step.
  if (seen.size >= SEEN_CAP) {
    flushImpressions();
    seen.clear();
  }
  seen.add(key);

  bindLifecycleListeners();

  let bySurface = pending.get(surface);
  if (!bySurface) {
    bySurface = new Map();
    pending.set(surface, bySurface);
  }
  bySurface.set(key, { entityType, entityId });
  pendingCount++;

  if (pendingCount >= FLUSH_AT_SIZE) flushImpressions();
  else scheduleTimer();
}

// Move everything pending into the shared telemetry buffer as one event per
// surface (split further only if a surface exceeds the per-event cap).
//
// `viaBeacon` reaches the shared buffer's beacon path rather than a normal fetch;
// it is set on the tab-hide path, where a plain fetch would be cancelled.
export function flushImpressions(viaBeacon = false): void {
  clearTimer();
  // Nothing pending means nothing to deliver, and no request. This is what keeps
  // an idle or abandoned tab free: the timer is only ever armed by recording an
  // impression, so a session that stops scrolling stops costing anything, and a
  // tab switched away from with an empty buffer sends no beacon at all.
  if (pendingCount === 0) return;

  const surfaces = Array.from(pending.entries());
  pending.clear();
  pendingCount = 0;

  const key = getSessionKey();
  for (const [surface, entityMap] of surfaces) {
    const entities = Array.from(entityMap.values());
    for (let i = 0; i < entities.length; i += IMPRESSION_ENTITIES_MAX) {
      enqueueTrackEvent({
        kind: 'impression',
        data: {
          sessionKey: key,
          surface,
          entities: entities.slice(i, i + IMPRESSION_ENTITIES_MAX),
        },
      });
    }
  }

  // Impressions are enqueued but never worth waking the shared buffer for on
  // their own — except on the way out, where "next flush" may not exist.
  if (viaBeacon) flushTrackEvents(true);
}

// 🔴 The 90s interval bounds the rate for a session that is SCROLLING. It is not
// a ceiling on requests per session, because tab-hide flushes too (below) and a
// tab switch is a hide. A user alt-tabbing repeatedly while reading a feed emits
// one request per switch that had impressions pending, not one per 90s.
//
// That is deliberate — the alternative is losing the buffer when the tab is
// discarded — but it means the interval is the dial for the steady-state rate
// only. The `pendingCount === 0` early return above is what keeps the tail of
// this bounded: switching away twice in a row costs one request, not two.

function bindLifecycleListeners() {
  if (listenersBound || typeof document === 'undefined' || typeof window === 'undefined') return;
  listenersBound = true;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushImpressions(true);
  });
  window.addEventListener('pagehide', () => flushImpressions(true));
}

// Test-only hooks, mirroring trackEventBuffer's. Not part of the runtime contract.
export const __impressionBufferTestHooks = {
  pendingCount: () => pendingCount,
  seenCount: () => seen.size,
  reset: () => {
    clearTimer();
    pending.clear();
    pendingCount = 0;
    seen.clear();
    sessionKey = null;
    listenersBound = false;
  },
  constants: { FLUSH_INTERVAL_MS, FLUSH_AT_SIZE, SEEN_CAP },
};
